import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import notify from 'devextreme/ui/notify';
import validationEngine from 'devextreme/ui/validation_engine';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { getDialogYesNoOptions, getNotificationTimeout, sendNotificationMessage } from '../../../types/resource';
import { LoggingService } from '../../../framework/logging/logging.service';
import { custom } from 'devextreme/ui/dialog';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { ProfileDataService } from '../../../shared/services/profile-data.service';
import { TaskDataService } from '../../../shared/services/task-data.service';
import { ITaskPipelineData, ITaskRequest, TaskPipelineData, TaskRequest } from '../../../modules/tasks/model/types';
import { MessageService } from '../../../shared/components/framework/services/message.service';
import { FormUtils } from '../../../shared/components/framework/services/form-builder.service';
import dxDataGrid from 'devextreme/ui/data_grid';

@Component({
  standalone: false,
  selector: 'app-pipeline',
  templateUrl: './pipeline.component.html',
  styleUrls: ['./pipeline.component.css']
})
export class PipelineComponent implements OnInit {
  @ViewChild('formElement') formElement!: ElementRef;

  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  pipelineData: ITaskPipelineData;
  files: File[] = [];
  isLoading: boolean = false;
  clients: any[] = [];
  sourceTypes: any[] = [];
  amAssignees: any[] = [];
  requestType: any;
  today: Date = new Date();
  isEditable: boolean = false;
  currentFormInstance: any = null;
  formColCountBehaviourSubject$ = new BehaviorSubject<number>(1);
  formColCount$ = this.formColCountBehaviourSubject$.asObservable();
  formColCount: number = 1;
  formItems: any[] = [];
  formItemsBehaviourSubject$ = new BehaviorSubject<any[]>([]);
  formItems$ = this.formItemsBehaviourSubject$.asObservable();
  lsaAssignment: string | null = null;
  loanNumbersGrid?: any;
  mandatoryFiles: boolean = true;

  constructor(private readonly loggingService: LoggingService, private readonly profileDataService: ProfileDataService
    , private readonly taskDataService: TaskDataService, private readonly messageService: MessageService) {
    this.onCustomClientCreating = this.onCustomClientCreating.bind(this);

    this.formItems$.subscribe((items: any[]) => {
      this.formItems = items;
    });

    this.formColCount$.subscribe((colcount: number) => {
      this.formColCount = colcount;
    });
  }

  async onSubmit(e: any)
  {
    e.preventDefault();
    let validationResult = validationEngine.validateGroup("formGroup");

    if (validationResult.isValid)
    {
      try{

        let request: ITaskRequest = new TaskRequest();
        let requestType = this.sourceTypes.find((t) => t.name == this.pipelineData.requestType);
        // Ensure nextTaxDueDate is explicitly included in the serialized data
        const requestJsonData = {
          ...this.pipelineData,
          lsaAssignment: this.pipelineData.lsaAssignment ?? null,
          amAssignee: this.pipelineData.amAssignee ?? null,
          loanNumbers: this.pipelineData.loanNumbersData ? this.pipelineData.loanNumbersData.map((ln: any) => ln.loanNumber) : []
        };
        request.templateId = requestType.id;
        request.name = requestType.name
        request.referenceName = this.pipelineData.dealName;
        request.referenceType = requestType.referenceType;

        let formData : FormData = new FormData()
        formData.append('templateId', request.templateId);
        formData.append('name', request.name);
        formData.append('referenceName', request.referenceName);
        formData.append('requestJsonData', this.removeProxyData(JSON.stringify(requestJsonData)));
        formData.append('referenceType', request.referenceType);

        if(this.isEditable)
          formData.append('parentId', this.task.parentId);

        this.files.forEach((f) => {formData.append('files', f, f.name)});
        this.isLoading = true;
        const response = await lastValueFrom(this.taskDataService.insertTaskAsFormData(formData));
        sendNotificationMessage(`${response[1].data[0].task.taskId} task created`, this.messageService);
        console.log('json: ', requestJsonData);

        this.isLoading = false;

        this.formElement.nativeElement.reset();
        this.formElement.nativeElement.submit();
      }
      catch(err: any)
      {
        this.isLoading = false;
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, 'error', getNotificationTimeout());
      }
    }
  }

  onFileUpload(file: File)
  {
    this.files.push(file);
  }

  onFileRemove(file: File)
   {
    if(file != null && file != undefined)
    {
      this.files = this.files.filter(f => f !== file);
    }
   }



  onCustomClientCreating(e: any) {
    const newValue = e.text;
    if (newValue && !this.clients.includes(newValue)) {
      const dialog = custom(getDialogYesNoOptions("Add New Client", "You are about to add a new client. Do you want to continue?"));
      dialog.show().then((result) => {
        if (result) {
          this.clients.unshift(newValue); // Add the new value to the start of the array
          this.clients = [...this.clients]; // Reassign the array to trigger change detection
          e.customItem = newValue; // Assign the string value directly to e.customItem

          // Set the selected index to 0
          setTimeout(() => {
            const selectBoxInstance = e.component; // Get the dxSelectBox instance
            selectBoxInstance.option('value', newValue); // Set the selected value to the new client
          });
        } else {
          e.customItem = null; // Ensure e.customItem is null if the user cancels
        }
      });
    } else {
      e.customItem = null; // Ensure e.customItem is null if the client already exists or newValue is invalid
    }
  }

  async ngOnInit() {
    try{
      // Initialize form data immediately
      this.isEditable = this.task != null ? true : false;

      if(this.isEditable)
      {
        this.pipelineData = FormUtils.mapFromTask<TaskPipelineData>(this.task, TaskPipelineData);
        // Initialize loanNumbersData from loanNumbers string if it exists
        if (this.pipelineData.loanNumbers && !this.pipelineData.loanNumbersData) {
          this.pipelineData.loanNumbersData = this.pipelineData.loanNumbers
            .split(',')
            .map(ln => ln.trim())
            .filter(ln => ln)
            .map(ln => ({ key: this.generateUniqueId(), loanNumber: ln }));
        } else if (!this.pipelineData.loanNumbersData) {
          this.pipelineData.loanNumbersData = [];
        }
        // console.log(this.pipelineData);
      }
      else{
         this.pipelineData = new TaskPipelineData();
         this.pipelineData.loanNumbersData = [];
      }

      // Build initial form configuration with empty data - renders form structure immediately
      this.buildFormConfiguration();

      // Fetch all data in parallel instead of sequentially
      const [amData, requestTypeData, clientData] = await Promise.all([
        lastValueFrom(this.profileDataService.getUsersByGroup(["Asset Management"])),
        lastValueFrom(this.taskDataService.getTaskTemplatesByType(this.taskTemplateName)),
        lastValueFrom(this.taskDataService.getClients())
      ]);

      this.amAssignees = amData[1][0].users;
      this.sourceTypes = requestTypeData[1];
      this.requestType = requestTypeData[1][0];
      this.clients = clientData[1];

      // Rebuild form configuration with loaded data
      this.buildFormConfiguration();

    }
    catch(err: any)
    {
      this.loggingService.logError(err, SeverityLevel.Error);
      notify(err.message, 'error', getNotificationTimeout());
    }
  }

  onFormContentReady(e: any): void {
    this.currentFormInstance = e.component;
  }

  // Generate unique IDs for grid rows
  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  private buildFormConfiguration(): void {
    this.mandatoryFiles = !this.isEditable; // Make file attachments mandatory for new requests, optional for edits
    const config = [
      {
        labelMode: 'static',
        dataField: 'requestType',
        editorType: 'dxSelectBox',
        cssClass: 'dx-widget',
        label: {
          text: 'Request Type'
        },
        editorOptions: {
          items: this.sourceTypes,
          valueExpr: 'name',
          displayExpr: 'name',
          required: true,
          searchMode: 'contains',
          searchEnabled: true,
          disabled: this.isEditable
        },
        validationRules: [
          {
            type: 'required',
            message: 'Request Type is required'
          }
        ]
      },
      {
        labelMode: 'static',
        dataField: 'dealName',
        editorType: 'dxTextBox',
        label: {
          text: 'Deal Name'
        },
        editorOptions: {
          required: true
        },
        validationRules: [
          {
            type: 'required',
            message: 'Deal Name is required'
          }
        ]
      },

      {
        labelMode: 'static',
        dataField: 'borrowerName',
        editorType: 'dxTextBox',
        cssClass: 'dx-widget',
        label: {
          text: 'Borrower'
        },
        editorOptions: {
          required: true
        },
        validationRules: [
          {
            type: 'required',
            message: 'Borrower is required'
          }
        ]
      },
      {
        itemType: "custom",
        name: "loanNumbersGrid",
        colSpan: 1,
        template: (formItemData, container) => {
          // Create main container
          const mainContainer = document.createElement("div");
          mainContainer.style.position = "relative";
          container.appendChild(mainContainer);

          // Create grid container
          const gridElement = document.createElement("div");
          gridElement.style.marginTop = "0px";
          mainContainer.appendChild(gridElement);

          // Initialize the data source if it doesn't exist
          if (!this.pipelineData.loanNumbersData) {
            this.pipelineData.loanNumbersData = [];
          }

          const grid = new dxDataGrid(gridElement, {
            dataSource: this.pipelineData.loanNumbersData,
            keyExpr: "key",
            columns: [
              {
                dataField: "loanNumber",
                caption: "Loan Number",
                dataType: "string",
                validationRules: [
                  {
                    type: "required",
                    message: "Loan number is required",
                  },
                  {
                    type: "custom",
                    message: "This loan number has already been added",
                    validationCallback: (e: any) => {
                      const newValue = (e.value || "").trim().toLowerCase();
                      if (!newValue) return true;
                      return !this.pipelineData.loanNumbersData.some(
                        (row: any) =>
                          row.key !== e.data?.key &&
                          (row.loanNumber || "").trim().toLowerCase() === newValue
                      );
                    },
                    reevaluate: true,
                  },
                ],
              },
            ],
            editing: {
              mode: "row",
              allowAdding: true,
              allowUpdating: true,
              allowDeleting: true,
              confirmDelete: false,
            },
            showBorders: true,
            columnAutoWidth: true,
            rowAlternationEnabled: true,
            onRowInserted: (e) => {
              // Add unique ID to the new row if it doesn't exist
              if (!e.data.key) {
                e.data.key = this.generateUniqueId();
              }
            },
            onRowUpdated: (e) => {
              // Data is automatically updated in the array
            },
            onRowRemoved: (e) => {
              // Data is automatically removed from the array
            },
          });

          // Store grid reference
          this.loanNumbersGrid = grid;
        },
      },
      {
        labelMode: 'static',
        dataField: 'client',
        editorType: 'dxSelectBox',
        label: {
          text: 'Client'
        },
        editorOptions: {
          items: this.clients,
          valueExpr: 'name',
          displayExpr: 'name',
          required: true,
          searchMode: 'contains',
          searchEnabled: true,
          acceptCustomValue: true,
          onCustomItemCreating: this.onCustomClientCreating,
          placeholder: '<New Client>'
        },
        validationRules: [
          {
            type: 'required',
            message: 'Client is required'
          }
        ]
      },
      {
        dataField: 'closingDate',
        editorType: 'dxDateBox',
        label: {
          text: 'Estimated Closing Date'
        },
        editorOptions: {
          required: false
        },
        validationRules: [
          {
            type: 'required',
            message: 'Estimated Closing Date is required'
          }
        ]
      },
      {
        itemType: "group",
        colCount: 4,
        colSpan: 2,
        items: [
          {
            labelMode: "static",
            dataField: "amAssigneeId",
            editorType: "dxSelectBox",
            colSpan: 4,
            cssClass: "dx-widget",
            label: { text: "AM Assignee *" },
            editorOptions: {
              items: this.amAssignees,
              valueExpr: "id",
              displayExpr: "name",
              required: false,
              searchMode: "contains",
              searchEnabled: true,
              onValueChanged: (e) => {
                if (e.value) {
                  const selectedUser = this.amAssignees.find(u => u.id === e.value);
                  if (selectedUser) {
                    this.pipelineData.amAssignee = selectedUser.name;
                    this.pipelineData.amAssigneeId = selectedUser.id;
                  }
                } else {
                  this.pipelineData.amAssignee = null;
                  this.pipelineData.amAssigneeId = null;
                }
              },
            },
          },
        ],
        validationRules: [
          { type: "required", message: "AM Assignee is required" },
        ],
      },
      {
        dataField: "lsaAssignment",
        editorType: "dxTextBox",
        visible: false,
        label: { text: "LSA Assignment" },
        colSpan: 1,
        cssClass: "dx-widget",
        editorOptions: {
          value: this.lsaAssignment,
          width: "100%",
        },
      },
      {
        dataField: "isUrgent",
        editorType: "dxCheckBox",
        label: { text: "Rush Job?" },
        colSpan: 1,
        cssClass: "dx-widget",
      },
      {
        dataField: "description",
        editorType: "dxTextArea",
        label: { text: "Description" },
        colSpan: 3,
        cssClass: "dx-widget",
        editorOptions: {
          required: "true",
          defaultValue: "",
          height: 100,
        },
      },
      // File Upload
            {
              // work around to bind file upload component to form and validate it
              dataField: "fileUploadProxy",
              colSpan: 2,
              itemType: "simple",
              label: { visible: false },
              editorType: "dxTextBox",
              editorOptions: {
                visible: false,
                value: this.files.length.toString(),
              },
              validationRules: [
                {
                  type: "custom",
                  message: "At least one file is required",
                  validationCallback: (e) => this.onValidateFileAttachments(e),
                  reevaluate: true,
                },
              ],
            },
            {
              colSpan: 2,
              horizontalAlignment: "center",
              itemType: "simple",
              name: "fileUpload",
              label: { text: "Upload Files" },
              template: "fileUploadTemplate",
            },
      {
        cssClass: 'borrow-submit',
        horizontalAlignment: 'center',
        editorType: 'dxButton',
        editorOptions: {
          text: 'Submit',
          useSubmitBehavior: true
        }
      }
    ];

    this.formItemsBehaviourSubject$.next(config);
  }

  onValidateFileAttachments(e: any): boolean {
    if (this.mandatoryFiles && this.files.length == 0) {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  removeProxyData(data: string): string {
    if(data) {
      let json = JSON.parse(data);
      delete json["fileUploadProxy"];
      delete json["invoiceBorrower"];
      delete json["invoiceClient"];
      delete json["invoiceNone"];
      delete json["invoiceValidationProxy"];
      delete json["loanNumbersProxy"];
      return JSON.stringify(json);
    }
    return "{}";
  }
}
